/**
 * Inventory, hotbar and crafting UI.
 *
 * DOM rather than in-world geometry: crisp text at any resolution, real
 * pointer events, and no font atlas to maintain. All the stacking, crafting
 * and click rules live in core/ - this file only draws them and reports clicks
 * back, so the interesting behaviour stays testable without a browser.
 */

import {
  Inventory, HOTBAR_SIZE, TOTAL_SLOTS, MAIN_SIZE,
  leftClickSlot, rightClickSlot, quickMove,
  type ItemStack,
} from "../core/inventory/inventory.js";
import {
  GRID_SIZE, GRID_SLOTS, matchRecipe, nearMiss, takeResult, clearGridInto,
  recipeName,
  recipeBlocked, STATION_LABELS, type Station,
} from "../core/crafting/recipes.js";
import {
  itemTile, itemLabel, itemHint, itemMaxStack, itemDef,
} from "../core/block/items.js";
import { pick, ui, type Lang } from "../core/text/lang.js";
import {
  palette, GROUP_ORDER, GROUP_LABELS, type PaletteGroup,
} from "../core/block/palette.js";
import { CHEST_SLOTS } from "../core/world/containers.js";
import {
  SLOT_INPUT, SLOT_FUEL, SLOT_OUTPUT, SMELT_SECONDS, furnaceAccepts,
  FURNACE_SLOTS, type Furnace,
} from "../core/world/furnace.js";
import {
  ARMOUR_SLOTS, ARMOUR_SLOT_LABELS, fitsSlot,
} from "../core/entity/armour.js";
import { itemArmour } from "../core/block/items.js";
import { Tooltip, type TooltipContent } from "./tooltip.js";
import { ATLAS_TILES } from "../core/block/tiles.js";
import { atlasDataUrl } from "../render/atlas.js";

/** Slot edge in CSS pixels. Must match --slot in the stylesheet. */
const SLOT_PX = 44;

const BOOK_STORAGE_KEY = "vyzhyvannia.recipeBook";
/** One key per shelf, suffixed with the group name. */
const SHELF_STORAGE_KEY = "vyzhyvannia.storeShelf";

/** Whether the book column was showing last time. Closed by default. */
function loadBookPreference(): boolean {
  try {
    return localStorage.getItem(BOOK_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Which set of slots a UI slot belongs to. */
type Container = "main" | "craft" | "result" | "chest" | "stove" | "armour";

export class InventoryUI {
  private readonly inventory: Inventory;
  /** The 3x3 crafting grid. A nine-slot Inventory, so clicks reuse the same rules. */
  private readonly grid: Inventory;

  private readonly hotbarEl: HTMLDivElement;
  private readonly panelEl: HTMLDivElement;
  private readonly heldEl: HTMLDivElement;
  private readonly resultEl: HTMLDivElement;
  private readonly recipeNameEl: HTMLDivElement;
  private readonly chestEl: HTMLDivElement;
  private readonly chestSlots: HTMLDivElement[] = [];
  /** The chest currently open, or null when the bag is open on its own. */
  private chest: Inventory | null = null;
  private readonly stoveEl: HTMLDivElement;
  private readonly stoveSlots: HTMLDivElement[] = [];
  private readonly flameEl: HTMLDivElement;
  private readonly cookEl: HTMLDivElement;
  /** The stove currently open, if the window was opened by clicking one. */
  private stove: Furnace | null = null;

  /** What is being worn. Four slots, in ARMOUR_SLOTS order. */
  private readonly armour: Inventory;
  private readonly armourSlots: HTMLDivElement[] = [];

  private readonly hotbarSlots: HTMLDivElement[] = [];
  /**
   * The name of what was just selected, over the hotbar for a moment.
   *
   * "When you switch a block from 1 to 9 it displays its name for 3
   * seconds." Nine small pictures are nine small pictures; the name is
   * what tells a глиняна плитка from a кам'яна at a glance, and it is
   * wanted at the moment of choosing and not after.
   */
  private readonly nameEl: HTMLDivElement;
  private nameTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly panelSlots: HTMLDivElement[] = [];
  private readonly gridSlots: HTMLDivElement[] = [];

  /** The stack riding on the cursor while rearranging. */
  private held: ItemStack | null = null;

  private selected = 0;
  /** Crafting stations within reach, refreshed by the game each frame. */
  private stations: ReadonlySet<Station> = new Set();
  open = false;

  onClose: (() => void) | null = null;
  /** Fired whenever anything changed, so the HUD can follow along. */
  onChange: (() => void) | null = null;
  /**
   * Throw a stack out into the world. Set by the game; null on its own.
   *
   * The panel knows what is under the cursor and what is riding on it; the
   * game knows where the player is standing and how hard a thing is thrown.
   * Neither has any business with the other's half, so the stack is handed
   * over and that is the end of the panel's interest in it.
   */
  onDrop: ((stack: ItemStack) => void) | null = null;
  /** Which slot the mouse is over, so Q knows what it is being asked about. */
  private hovered: { index: number; container: Container } | null = null;

  private readonly atlasUrl: string;
  private readonly tooltip: Tooltip;
  private readonly bookButton: HTMLButtonElement;
  private readonly storeButton: HTMLButtonElement;
  private readonly questButton: HTMLButtonElement;
  /** The creative store: every block there is, free for the taking. */
  private readonly storeEl: HTMLDivElement;
  private storeBuilt = false;
  private storeOpen = false;
  /** Whether the store exists at all. Off unless the player asked for it. */
  private creative = false;

  /** Which language every caption here is written in. Set by main.ts. */
  lang: Lang = "uk";
  /*
   * The fixed captions, kept so the language can change without a reload.
   *
   * Everything else in this panel is redrawn by `refresh` and picks the
   * language up on its own; these are written once when the window is built,
   * and would otherwise stay in whatever language it was built in.
   */
  private readonly titleEl: HTMLDivElement;
  private readonly chestTitleEl: HTMLDivElement;
  private readonly stoveTitleEl: HTMLDivElement;
  private readonly armourTitleEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;

  /** Where the recipe book mounts. Owned here so there is only one window. */
  readonly bookHost: HTMLDivElement;
  /** Where the книга пригод mounts. The third thing that can hold this half. */
  readonly questHost: HTMLDivElement;

  /** Fired when the book column is shown or hidden. */
  onBookToggle: ((open: boolean) => void) | null = null;
  /** Told when the книга пригод is opened, so it can redraw before it is seen. */
  onQuestsToggle: ((open: boolean) => void) | null = null;

  constructor(
    inventory: Inventory, grid: Inventory, armour: Inventory,
    root: HTMLElement, tooltip: Tooltip,
  ) {
    this.inventory = inventory;
    this.grid = grid;
    this.armour = armour;
    this.tooltip = tooltip;
    this.atlasUrl = atlasDataUrl();

    // --- hotbar strip, always on screen ---------------------------------
    this.nameEl = document.createElement("div");
    this.nameEl.id = "hotbar-name";
    this.hotbarEl = document.createElement("div");
    this.hotbarEl.id = "hotbar";
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const el = this.makeSlot(i, "main", false);
      this.hotbarSlots.push(el);
      this.hotbarEl.appendChild(el);
    }

    // --- the panel -------------------------------------------------------
    // One window, not two. The recipe book exists to serve the crafting grid,
    // so it lives beside it rather than docked to the far edge of the screen
    // with a life cycle of its own to keep in step.
    this.panelEl = document.createElement("div");
    this.panelEl.id = "inventory";
    this.panelEl.hidden = true;

    const header = document.createElement("div");
    header.className = "inv-header";

    const title = document.createElement("div");
    title.className = "inv-title";
    this.titleEl = title;

    this.bookButton = document.createElement("button");
    this.bookButton.type = "button";
    this.bookButton.className = "inv-button";
    this.bookButton.textContent = ui("inv-book", this.lang);
    this.bookButton.addEventListener("click", () => this.toggleBook());

    this.storeButton = document.createElement("button");
    this.storeButton.type = "button";
    this.storeButton.className = "inv-button";
    this.storeButton.textContent = ui("inv-store", this.lang);
    this.storeButton.hidden = true;
    this.storeButton.addEventListener("click", () => this.toggleStore());

    this.questButton = document.createElement("button");
    this.questButton.type = "button";
    this.questButton.className = "inv-button";
    this.questButton.textContent = ui("inv-quests", this.lang);
    this.questButton.addEventListener("click", () => this.toggleQuests());

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "inv-button inv-close";
    closeButton.textContent = "✕";
    closeButton.title = ui("inv-close", this.lang);
    closeButton.addEventListener("click", () => this.close());

    header.append(title, this.storeButton, this.questButton, this.bookButton,
      closeButton);

    // The chest, above everything else and hidden until one is open. Its slots
    // are built once and rebound to whichever chest was clicked, because
    // building twenty-seven elements per opening would be twenty-seven
    // elements of garbage per opening.
    this.chestEl = document.createElement("div");
    this.chestEl.className = "inv-chest";
    this.chestEl.hidden = true;
    const chestTitle = document.createElement("div");
    chestTitle.className = "inv-subtitle";
    this.chestTitleEl = chestTitle;
    const chestGrid = document.createElement("div");
    chestGrid.className = "inv-grid";
    for (let i = 0; i < CHEST_SLOTS; i++) {
      const el = this.makeSlot(i, "chest", true);
      this.chestSlots.push(el);
      chestGrid.appendChild(el);
    }
    this.chestEl.append(chestTitle, chestGrid);

    // The піч: what is cooking, what is burning, and what has come out. Laid
    // out the way it works - input above fuel, both feeding the result - so
    // the picture teaches the rule.
    this.stoveEl = document.createElement("div");
    this.stoveEl.className = "inv-stove";
    this.stoveEl.hidden = true;
    const stoveTitle = document.createElement("div");
    stoveTitle.className = "inv-subtitle";
    this.stoveTitleEl = stoveTitle;

    const stoveRow = document.createElement("div");
    stoveRow.className = "stove-row";
    const stoveLeft = document.createElement("div");
    stoveLeft.className = "stove-feed";
    for (const slot of [SLOT_INPUT, SLOT_FUEL]) {
      const el = this.makeSlot(slot, "stove", true);
      this.stoveSlots[slot] = el;
      stoveLeft.append(el);
    }
    this.flameEl = document.createElement("div");
    this.flameEl.className = "stove-flame";
    stoveLeft.insertBefore(this.flameEl, this.stoveSlots[SLOT_FUEL]!);

    this.cookEl = document.createElement("div");
    this.cookEl.className = "stove-cook";
    const outSlot = this.makeSlot(SLOT_OUTPUT, "stove", true);
    this.stoveSlots[SLOT_OUTPUT] = outSlot;
    stoveRow.append(stoveLeft, this.cookEl, outSlot);
    this.stoveEl.append(stoveTitle, stoveRow);

    const craftRow = document.createElement("div");
    craftRow.className = "craft-row";

    const gridEl = document.createElement("div");
    gridEl.className = "craft-grid";
    for (let i = 0; i < GRID_SLOTS; i++) {
      const el = this.makeSlot(i, "craft", true);
      this.gridSlots.push(el);
      gridEl.appendChild(el);
    }

    const arrow = document.createElement("div");
    arrow.className = "craft-arrow";
    arrow.textContent = "→";

    const resultWrap = document.createElement("div");
    resultWrap.className = "craft-result";
    this.resultEl = this.makeSlot(0, "result", true);
    this.recipeNameEl = document.createElement("div");
    this.recipeNameEl.className = "craft-name";
    resultWrap.append(this.resultEl, this.recipeNameEl);

    // What is worn, beside the grid rather than in a window of its own: it is
    // read at the same moments the bag is, and a second window would be a
    // second thing to open before a raid.
    const armourEl = document.createElement("div");
    armourEl.className = "inv-armour";
    const armourTitle = document.createElement("div");
    armourTitle.className = "inv-subtitle";
    this.armourTitleEl = armourTitle;
    const armourGrid = document.createElement("div");
    armourGrid.className = "armour-column";
    for (let i = 0; i < ARMOUR_SLOTS.length; i++) {
      const el = this.makeSlot(i, "armour", true);
      el.classList.add("slot-armour");
      el.dataset.wear = pick(ARMOUR_SLOT_LABELS[ARMOUR_SLOTS[i]!], this.lang);
      this.armourSlots.push(el);
      armourGrid.appendChild(el);
    }
    armourEl.append(armourTitle, armourGrid);

    craftRow.append(gridEl, arrow, resultWrap, armourEl);

    const mainGridEl = document.createElement("div");
    mainGridEl.className = "inv-grid";
    const panelHotbarEl = document.createElement("div");
    panelHotbarEl.className = "inv-grid inv-hotbar-row";

    for (let i = HOTBAR_SIZE; i < TOTAL_SLOTS; i++) {
      const el = this.makeSlot(i, "main", true);
      this.panelSlots[i] = el;
      mainGridEl.appendChild(el);
    }
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const el = this.makeSlot(i, "main", true);
      this.panelSlots[i] = el;
      panelHotbarEl.appendChild(el);
    }

    const hint = document.createElement("div");
    hint.className = "inv-hint";
    hint.textContent =
      "";
    this.hintEl = hint;

    const left = document.createElement("div");
    left.className = "inv-left";
    left.append(this.chestEl, this.stoveEl, craftRow, mainGridEl, panelHotbarEl, hint);

    // The book mounts in here. Hidden by default so the window stays small
    // until it is wanted, and the choice is remembered.
    this.bookHost = document.createElement("div");
    this.bookHost.className = "inv-book";
    this.bookHost.hidden = true;

    // The store sits where the recipe book does, and only one of the two is
    // ever open: they are both "the other half of the window", and two of
    // them side by side would not fit on a laptop screen.
    this.storeEl = document.createElement("div");
    this.storeEl.className = "inv-store";
    this.storeEl.hidden = true;

    // And the книга пригод, which is the third thing that can have this half
    // of the window. Never two at once - see `setBookOpen`.
    this.questHost = document.createElement("div");
    this.questHost.className = "inv-quests";
    this.questHost.hidden = true;

    const body = document.createElement("div");
    body.className = "inv-body";
    body.append(left, this.bookHost, this.questHost, this.storeEl);

    this.panelEl.append(header, body);

    this.heldEl = document.createElement("div");
    this.heldEl.id = "held-stack";
    this.heldEl.hidden = true;

    root.append(this.nameEl, this.hotbarEl, this.panelEl, this.heldEl);
    document.addEventListener("mousemove", this.onMouseMove);
    this.refresh();
  }

  private makeSlot(index: number, container: Container, clickable: boolean): HTMLDivElement {
    const el = document.createElement("div");
    el.className = container === "result" ? "slot slot-result" : "slot";
    el.dataset.index = String(index);

    const icon = document.createElement("div");
    icon.className = "slot-icon";
    const count = document.createElement("span");
    count.className = "slot-count";
    el.append(icon, count);

    if (clickable) {
      el.addEventListener("mousedown", (e) => {
        e.preventDefault();
        // Ctrl (or shift, for anyone who has played the other one) turns the
        // left button into "send this somewhere sensible".
        if (e.button === 0 && (e.ctrlKey || e.metaKey || e.shiftKey)) {
          this.quickMoveFrom(index, container);
          return;
        }
        this.onSlotClick(index, container, e.button);
      });
      el.addEventListener("contextmenu", (e) => e.preventDefault());
      el.addEventListener("mouseenter", () => {
        this.hovered = { index, container };
      });
      el.addEventListener("mouseleave", () => {
        if (this.hovered?.index === index &&
            this.hovered.container === container) {
          this.hovered = null;
        }
      });
      this.tooltip.attach(el, () => this.describe(index, container));
    }
    return el;
  }

  /** What the tooltip should say about a slot right now, or null if empty. */
  private describe(index: number, container: Container): TooltipContent | null {
    const stack = container === "result"
      ? matchRecipe(this.grid, this.mapId)?.result ?? null
      : this.slotsOf(container)?.get(index) ?? null;
    if (stack === null) {
      // An empty armour slot still has something to say - which piece goes
      // here - and that is the only place in the window where an empty slot
      // is not self-explanatory.
      if (container !== "armour") return null;
      const wear = ARMOUR_SLOTS[index];
      return wear === undefined
        ? null : { title: pick(ARMOUR_SLOT_LABELS[wear], this.lang) };
    }

    const hint = itemHint(stack.id, this.lang);
    return {
      title: stack.count > 1
        ? `${itemLabel(stack.id, this.lang)} × ${stack.count}`
        : itemLabel(stack.id, this.lang),
      ...(hint !== undefined ? { hint } : {}),
    };
  }

  /**
   * Which set of slots a container's clicks act on.
   *
   * The stove used to fall through to `this.inventory` here, so clicking its
   * fuel slot rearranged hotbar slot 1 while the stove panel went on drawing
   * the stove. Naming every case and returning null for the rest is what
   * stops the next container from inheriting that.
   */
  private slotsOf(container: Container): Inventory | null {
    switch (container) {
      case "craft": return this.grid;
      case "chest": return this.chest;
      case "stove": return this.stove?.items ?? null;
      case "armour": return this.armour;
      case "main": return this.inventory;
      case "result": return null;
    }
  }

  /**
   * Whether what is on the cursor may be put down here.
   *
   * Taking out is always allowed - it is only putting in that a slot can
   * refuse - so a null cursor passes everything.
   */
  private accepts(index: number, container: Container, stack: ItemStack | null): boolean {
    if (stack === null) return true;
    if (container === "armour") {
      return fitsSlot(itemArmour(stack.id), ARMOUR_SLOTS[index] ?? "head");
    }
    if (container === "stove") return furnaceAccepts(index, stack.id);
    return true;
  }

  /**
   * Ctrl+click: move a whole stack to wherever it obviously belongs.
   *
   * "Obviously" is the whole design of this. With a chest open, the bag and
   * the chest are each other's other side and nothing else is in question.
   * With a stove open it is the stove. With neither, the bag folds onto
   * itself - hotbar to the shelves and back - which is what the gesture does
   * everywhere else it exists, and doing nothing at all there would read as
   * the feature being broken rather than as it not applying.
   *
   * Armour goes on last of the three, and only from the bag: a piece dragged
   * out of a chest should land in the bag, where the player can see it.
   */
  private quickMoveFrom(index: number, container: Container): void {
    // Nothing to do with the result slot: taking a craft is its own gesture,
    // and "craft as many as the materials allow" is a different feature.
    if (container === "result") return;

    const source = this.slotsOf(container);
    if (source === null) return;
    const stack = source.get(index);
    if (stack === null) return;

    let moved = 0;
    if (container === "main") {
      // Out of the bag: into whatever is open, and only then onto the body.
      if (this.chest !== null) {
        moved = quickMove(source, index, this.chest);
      } else if (this.stove !== null) {
        const stove = this.stove;
        moved = quickMove(
          source, index, stove.items, 0, FURNACE_SLOTS,
          (slot, id) => furnaceAccepts(slot, id),
        );
      } else if (itemArmour(stack.id) !== undefined) {
        moved = quickMove(
          source, index, this.armour, 0, ARMOUR_SLOTS.length,
          (slot, id) => fitsSlot(itemArmour(id), ARMOUR_SLOTS[slot] ?? "head"),
        );
      }
      if (moved === 0) {
        // Nowhere else to go: fold the bag onto itself, hotbar to shelves
        // and back, which is what this gesture does with no container open.
        const toHotbar = index >= HOTBAR_SIZE;
        moved = quickMove(
          source, index, source,
          toHotbar ? 0 : HOTBAR_SIZE,
          toHotbar ? HOTBAR_SIZE : HOTBAR_SIZE + MAIN_SIZE,
        );
      }
    } else {
      // Out of a chest, a stove or the crafting grid: into the bag.
      moved = quickMove(source, index, this.inventory);
    }

    if (moved === 0) return;
    this.refresh();
    this.onChange?.();
  }

  private onSlotClick(index: number, container: Container, button: number): void {
    if (container === "result") {
      // Only the left button takes a result; there is no half-crafting.
      if (button === 0) this.held = takeResult(this.grid, this.held, this.stations);
    } else {
      const target = this.slotsOf(container);
      // Clicking a chest slot with no chest open cannot happen - the panel is
      // hidden - but the type says it can, and returning is cheaper than a
      // non-null assertion that would be a crash if it ever did.
      if (target === null) return;
      if (!this.accepts(index, container, this.held)) return;
      if (button === 0) this.held = leftClickSlot(target, index, this.held);
      else if (button === 2) this.held = rightClickSlot(target, index, this.held);
      else return;
    }
    this.refresh();
    this.onChange?.();
  }

  /**
   * Throw away what the cursor is on, or what it is carrying.
   *
   * What Q does while the bag is open. The stack riding on the cursor comes
   * first - if there is one, that is plainly the thing being pointed at -
   * and after that whatever slot the mouse is over. Returns false when there
   * is nothing to throw, so the caller can fall back to throwing what is in
   * the hand instead.
   */
  dropUnderCursor(whole: boolean): boolean {
    if (this.onDrop === null) return false;
    if (this.held !== null) {
      const count = whole ? this.held.count : 1;
      this.onDrop({ id: this.held.id, count });
      this.held = this.held.count > count
        ? { id: this.held.id, count: this.held.count - count }
        : null;
      this.refresh();
      this.onChange?.();
      return true;
    }
    const at = this.hovered;
    if (at === null || at.container === "result") return false;
    const target = this.slotsOf(at.container);
    if (target === null) return false;
    const stack = target.get(at.index);
    if (stack === null) return false;
    const count = whole ? stack.count : 1;
    const taken = target.takeFromSlot(at.index, count);
    if (taken === 0) return false;
    this.onDrop({ id: stack.id, count: taken });
    this.refresh();
    this.onChange?.();
    return true;
  }

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.open || this.held === null) return;
    this.heldEl.style.left = `${e.clientX}px`;
    this.heldEl.style.top = `${e.clientY}px`;
  };

  /**
   * Repaint the stove, and nothing else.
   *
   * Called every frame while the window is open, because the thing it is
   * showing changes on its own. A full refresh() every frame would rebuild
   * every slot in the bag sixty times a second to animate two bars.
   */
  refreshStove(): void {
    if (this.stove === null) return;
    for (const slot of [SLOT_INPUT, SLOT_FUEL, SLOT_OUTPUT]) {
      this.paintSlot(this.stoveSlots[slot]!, this.stove.items.get(slot));
    }
    const fire = this.stove.burnMax > 0 ? this.stove.burn / this.stove.burnMax : 0;
    this.flameEl.style.setProperty("--fill", `${Math.round(fire * 100)}%`);
    this.flameEl.classList.toggle("lit", this.stove.lit);
    const done = Math.min(1, this.stove.cook / SMELT_SECONDS);
    this.cookEl.style.setProperty("--fill", `${Math.round(done * 100)}%`);
  }

  /** CSS that crops one tile out of the shared atlas image. */
  private applyIcon(icon: HTMLElement, id: number): void {
    if (id === 0) {
      icon.style.backgroundImage = "";
      return;
    }
    // Blocks show their top face; items have a tile of their own.
    const tile = itemTile(id);
    const col = tile % ATLAS_TILES;
    const row = Math.floor(tile / ATLAS_TILES);
    const sheet = SLOT_PX * ATLAS_TILES;
    icon.style.backgroundImage = `url(${this.atlasUrl})`;
    icon.style.backgroundSize = `${sheet}px ${sheet}px`;
    icon.style.backgroundPosition = `${-col * SLOT_PX}px ${-row * SLOT_PX}px`;
  }

  private paintSlot(el: HTMLDivElement, stack: ItemStack | null): void {
    const icon = el.firstElementChild as HTMLElement;
    const count = el.lastElementChild as HTMLElement;

    if (stack === null) {
      icon.style.backgroundImage = "";
      count.textContent = "";
      return;
    }
    this.applyIcon(icon, stack.id);
    // A lone item needs no "1" cluttering the slot. The name lives in the
    // tooltip now - the native `title` attribute would double up with it.
    count.textContent = stack.count > 1 ? String(stack.count) : "";
  }

  /** Redraw everything from the inventory. Cheap enough to do on any change. */
  refresh(): void {
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const el = this.hotbarSlots[i]!;
      this.paintSlot(el, this.inventory.get(i));
      el.classList.toggle("selected", i === this.selected);
    }
    for (let i = 0; i < TOTAL_SLOTS; i++) {
      const el = this.panelSlots[i];
      if (el === undefined) continue;
      this.paintSlot(el, this.inventory.get(i));
      el.classList.toggle("selected", i === this.selected);
    }
    for (let i = 0; i < GRID_SLOTS; i++) {
      this.paintSlot(this.gridSlots[i]!, this.grid.get(i));
    }
    for (let i = 0; i < this.armourSlots.length; i++) {
      this.paintSlot(this.armourSlots[i]!, this.armour.get(i));
    }
    if (this.chest !== null) {
      for (let i = 0; i < CHEST_SLOTS; i++) {
        this.paintSlot(this.chestSlots[i]!, this.chest.get(i));
      }
    }
    if (this.stove !== null) {
      for (const slot of [SLOT_INPUT, SLOT_FUEL, SLOT_OUTPUT]) {
        this.paintSlot(this.stoveSlots[slot]!, this.stove.items.get(slot));
      }
      // Two gauges: how much fire is left, and how far along the thing is.
      const fire = this.stove.burnMax > 0 ? this.stove.burn / this.stove.burnMax : 0;
      this.flameEl.style.setProperty("--fill", `${Math.round(fire * 100)}%`);
      this.flameEl.classList.toggle("lit", this.stove.lit);
      const done = Math.min(1, this.stove.cook / SMELT_SECONDS);
      this.cookEl.style.setProperty("--fill", `${Math.round(done * 100)}%`);
    }

    const recipe = matchRecipe(this.grid, this.mapId);
    const blocked = recipe === null ? null : recipeBlocked(recipe, this.stations);

    this.paintSlot(this.resultEl, recipe === null ? null : recipe.result);
    // Shown greyed with a reason rather than hidden: a player whose layout is
    // correct but who has no fire would otherwise rearrange it forever.
    this.resultEl.classList.toggle("ready", recipe !== null && blocked === null);
    this.resultEl.classList.toggle("blocked", blocked !== null);
    // With nothing matching, say whether the makings are there but stacked.
    // The grid counts occupied cells rather than items, and an unmatched grid
    // that is silent about it looks exactly like a broken game.
    const almost = recipe === null ? nearMiss(this.grid) : null;
    const L = this.lang;
    this.recipeNameEl.textContent =
      recipe !== null && blocked !== null
        ? `${ui("craft-needs", L)} ${pick(STATION_LABELS[blocked], L)}`
      : recipe !== null ? recipeName(recipe, L)
      // Shapeless wants one per cell; shaped wants a shape, and telling
      // somebody to spread out a брусок of clay is only half the truth.
      : almost !== null
        ? `${recipeName(almost, L)}: ${ui(almost.kind === "shapeless"
            ? "craft-one-each" : "craft-shape", L)}`
      : "";
    this.recipeNameEl.classList.toggle("hint", recipe === null && almost !== null);

    this.tooltip.suppressed = this.held !== null;
    if (this.held === null) {
      this.heldEl.hidden = true;
    } else {
      this.tooltip.hide();
      this.heldEl.hidden = false;
      this.heldEl.innerHTML = "";
      const icon = document.createElement("div");
      icon.className = "slot-icon";
      this.applyIcon(icon, this.held.id);
      const count = document.createElement("span");
      count.className = "slot-count";
      count.textContent = this.held.count > 1 ? String(this.held.count) : "";
      this.heldEl.append(icon, count);
    }
  }

  /** Is the recipe book column showing? */
  /** Tell the panel which stations are in reach. */
  /**
   * Which map this is, so recipes that belong to one can be hidden.
   *
   * A танк is «Сучасна війна»'s and nothing else's - see `recipeOffMap` -
   * and the bag has to know which map it is open on to leave it out.
   */
  setMap(mapId: string): void {
    this.mapId = mapId;
    this.refresh();
  }

  private mapId = "";

  setStations(stations: ReadonlySet<Station>): void {
    // Only redraw when it actually changed - this is called every frame.
    if (stations.size === this.stations.size &&
        [...stations].every((st) => this.stations.has(st))) return;
    this.stations = stations;
    this.refresh();
    this.onStationsChange?.(stations);
  }

  /** Fired when the stations in reach change, so the book can restyle. */
  onStationsChange: ((stations: ReadonlySet<Station>) => void) | null = null;

  get bookOpen(): boolean {
    return !this.bookHost.hidden;
  }

  /**
   * Show or hide the book column.
   *
   * Remembered across sessions: someone who crafts constantly should not have
   * to reopen it every time, and someone who never does should not have the
   * window be twice as wide for no reason.
   */
  setBookOpen(open: boolean): void {
    // The store shares this half of the window; opening one shuts the other.
    if (open && this.storeOpen) this.setStoreOpen(false);
    if (open) this.setQuestsOpen(false);
    this.bookHost.hidden = !open;
    this.bookButton.classList.toggle("active", open);
    this.tooltip.hide();
    try {
      localStorage.setItem(BOOK_STORAGE_KEY, open ? "1" : "0");
    } catch {
      // Not remembering the choice is not worth failing over.
    }
    this.onBookToggle?.(open);
  }

  toggleBook(): void {
    this.setBookOpen(!this.bookOpen);
  }

  /**
   * Whether the creative store exists.
   *
   * A switch rather than a build-time thing: the same game is played both
   * ways, and the button appears and disappears with the checkbox.
   */
  setCreative(on: boolean): void {
    this.creative = on;
    this.storeButton.hidden = !on;
    if (!on) this.setStoreOpen(false);
  }

  /**
   * Show or hide the store.
   *
   * Built the first time it is opened rather than at startup: it is a hundred
   * slots with an icon each, and a player who never turns creative on should
   * never pay for them.
   */
  setStoreOpen(open: boolean): void {
    if (open && !this.creative) return;
    this.storeOpen = open;
    if (open && !this.storeBuilt) this.buildStore();
    this.storeEl.hidden = !open;
    this.storeButton.classList.toggle("active", open);
    // One or the other. Both at once does not fit on a laptop screen.
    if (open) { this.setBookOpen(false); this.setQuestsOpen(false); }
    this.tooltip.hide();
  }

  /** Whether the книга пригод is the one holding this half of the window. */
  get questsOpen(): boolean {
    return !this.questHost.hidden;
  }

  /**
   * Show or hide the книга пригод.
   *
   * Not remembered across sessions, unlike the recipe book. The book of
   * recipes is a thing you work with constantly; the quest book is a thing
   * you look at, see where you are, and shut - and a window that opens a page
   * wide every time because you read it once a week is a window in the way.
   */
  setQuestsOpen(open: boolean): void {
    if (open) {
      this.setBookOpen(false);
      if (this.storeOpen) this.setStoreOpen(false);
    }
    this.questHost.hidden = !open;
    this.questButton.classList.toggle("active", open);
    this.tooltip.hide();
    this.onQuestsToggle?.(open);
  }

  toggleQuests(): void {
    this.setQuestsOpen(!this.questsOpen);
  }

  /**
   * Write every fixed caption again, in the language now in force.
   *
   * The store is thrown away rather than re-captioned: it is a hundred slots
   * with a heading over each shelf, and rebuilding it on the rare occasion
   * somebody changes language is simpler than walking it.
   */
  retitle(): void {
    this.titleEl.textContent = ui("inv-title", this.lang);
    this.bookButton.textContent = ui("inv-book", this.lang);
    this.storeButton.textContent = ui("inv-store", this.lang);
    this.chestTitleEl.textContent = ui("inv-chest", this.lang);
    this.stoveTitleEl.textContent = ui("inv-stove", this.lang);
    this.armourTitleEl.textContent = ui("inv-armour", this.lang);
    this.hintEl.textContent = ui("inv-help", this.lang);
    for (let i = 0; i < this.armourSlots.length; i++) {
      this.armourSlots[i]!.dataset.wear =
        pick(ARMOUR_SLOT_LABELS[ARMOUR_SLOTS[i]!], this.lang);
    }
    if (this.storeBuilt) {
      this.storeEl.replaceChildren();
      this.storeBuilt = false;
      if (this.storeOpen) this.buildStore();
    }
    this.refresh();
  }

  toggleStore(): void {
    this.setStoreOpen(!this.storeOpen);
  }

  /**
   * Fill the store with every holdable thing, in shelves.
   *
   * What is on it comes from core/block/palette.ts, which is built by walking
   * the item table rather than by listing what seemed useful - a store that
   * quietly leaves something out is worse than no store, because the one
   * thing it is for is reaching what you cannot otherwise get.
   */
  private buildStore(): void {
    this.storeBuilt = true;
    const title = document.createElement("div");
    title.className = "inv-subtitle";
    title.textContent = ui("inv-store-title", this.lang);
    this.storeEl.append(title);

    /*
     * The search box, under the heading and above the shelves.
     *
     * `type="search"` so a phone offers the right keyboard and the browser
     * draws its own clear button. The game's keys are held off while it has
     * focus - see `platform/input.ts` - which is why typing "e" into it no
     * longer shuts the whole window.
     */
    const search = document.createElement("input");
    search.type = "search";
    search.className = "store-search";
    search.id = "store-search";
    search.placeholder = ui("store-search", this.lang);
    search.autocomplete = "off";
    search.addEventListener("input", () => {
      this.applyFilter();
      this.tooltip.hide();
    });
    /*
     * Escape gets you out of the box, and no further.
     *
     * With the box focused the game's keys are letters - which is right, and
     * means «E» no longer shuts the bag while you are typing in it. Escape is
     * the way out of a text field everywhere else, so it is the way out of
     * this one: it clears what you typed and hands the keyboard back to the
     * game rather than opening the pause menu behind the bag.
     */
    search.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      if (search.value !== "") {
        search.value = "";
        this.applyFilter();
      }
      search.blur();
    });
    this.searchEl = search;
    this.storeEl.append(search);

    const empty = document.createElement("div");
    empty.className = "store-empty";
    empty.textContent = ui("store-none", this.lang);
    empty.hidden = true;
    this.emptyEl = empty;

    this.shelves.length = 0;
    const all = palette();
    for (const group of GROUP_ORDER) {
      const inGroup = all.filter((e) => e.group === group);
      if (inGroup.length === 0) continue;

      /*
       * Each shelf folds away.
       *
       * A hundred and twenty squares in one column is a scroll, not a store:
       * finding the block you came for meant reading past everything else.
       * Folded, the seven headings fit on one screen and you open the one you
       * want.
       *
       * A button rather than a div with a click handler, so it can be reached
       * and worked from the keyboard like any other control.
       */
      const shelfName = document.createElement("button");
      shelfName.type = "button";
      shelfName.className = "store-shelf";
      const caret = document.createElement("span");
      caret.className = "store-caret";
      const shelfText = document.createElement("span");
      shelfText.textContent = ui(GROUP_LABELS[group], this.lang);
      const shelfCount = document.createElement("span");
      shelfCount.className = "store-count";
      shelfCount.textContent = String(inGroup.length);
      shelfName.append(caret, shelfText, shelfCount);

      const shelf = document.createElement("div");
      shelf.className = "inv-grid store-grid";

      const open = this.shelfOpen(group);
      shelf.hidden = !open;
      shelfName.classList.toggle("shut", !open);
      shelfName.addEventListener("click", () => {
        const nowOpen = shelf.hidden;
        shelf.hidden = !nowOpen;
        shelfName.classList.toggle("shut", !nowOpen);
        this.rememberShelf(group, nowOpen);
        this.tooltip.hide();
      });

      const items: { el: HTMLDivElement; text: string }[] = [];
      for (const entry of inGroup) {
        const el = document.createElement("div");
        el.className = "slot";
        const icon = document.createElement("div");
        icon.className = "slot-icon";
        this.applyIcon(icon, entry.id);
        const count = document.createElement("span");
        count.className = "slot-count";
        el.append(icon, count);
        el.addEventListener("mousedown", (e) => {
          e.preventDefault();
          this.takeFromStore(entry.id, e);
        });
        el.addEventListener("contextmenu", (e) => e.preventDefault());
        this.tooltip.attach(el, () => {
          const hint = itemHint(entry.id, this.lang);
          return {
            title: itemLabel(entry.id, this.lang),
            ...(hint !== undefined ? { hint } : {}),
          };
        });
        shelf.append(el);
        /*
         * What this square answers to: its name in both languages and the
         * internal one. Lower-cased once here rather than on every keystroke.
         */
        items.push({
          el,
          text: [
            itemLabel(entry.id, "uk"), itemLabel(entry.id, "en"),
            itemDef(entry.id).name,
          ].join(" ").toLowerCase(),
        });
      }
      this.shelves.push({
        group, head: shelfName, grid: shelf, count: shelfCount, items,
      });
      this.storeEl.append(shelfName, shelf);
    }
    this.storeEl.append(empty);
  }

  /**
   * The search box, and what it does to the shelves.
   *
   * "Додай пошук по назві." A hundred and fifty-five things on seven folded
   * shelves is a store you can read, and still a store where finding «мазана
   * плита» means knowing which shelf a плита is on. Typing a few letters is
   * how anybody actually looks for a thing they can name.
   *
   * It matches the label in *both* languages plus the internal name, so
   * "wheat", "пшениц" and "wheat_seeds" all find the same thing - somebody
   * playing in Ukrainian who knows the English name should not have to
   * change language to use it.
   */
  private searchEl: HTMLInputElement | null = null;
  private emptyEl: HTMLDivElement | null = null;
  private readonly shelves: {
    group: PaletteGroup;
    head: HTMLButtonElement;
    grid: HTMLDivElement;
    count: HTMLSpanElement;
    items: { el: HTMLDivElement; text: string }[];
  }[] = [];

  private applyFilter(): void {
    const query = (this.searchEl?.value ?? "").trim().toLowerCase();
    let found = 0;
    for (const shelf of this.shelves) {
      let showing = 0;
      for (const item of shelf.items) {
        const hit = query === "" || item.text.includes(query);
        item.el.hidden = !hit;
        if (hit) showing++;
      }
      found += showing;
      shelf.count.textContent = String(showing);
      /*
       * A shelf with a match in it opens itself, and closes again when the
       * box is cleared - back to however the player had left it. Searching
       * must not quietly rearrange the store you come back to.
       */
      const open = query === "" ? this.shelfOpen(shelf.group) : showing > 0;
      shelf.grid.hidden = !open;
      shelf.head.classList.toggle("shut", !open);
      shelf.head.hidden = query !== "" && showing === 0;
    }
    if (this.emptyEl !== null) {
      this.emptyEl.hidden = query === "" || found > 0;
    }
  }

  /**
   * Which shelves are folded away, remembered between sessions.
   *
   * Per-viewer convenience and nothing more, so browser storage is the right
   * home for it and a browser that refuses to store anything simply opens
   * every shelf - which is what the store did before it could fold at all.
   */
  private shelfOpen(group: PaletteGroup): boolean {
    try {
      return localStorage.getItem(`${SHELF_STORAGE_KEY}.${group}`) !== "0";
    } catch {
      return true;
    }
  }

  private rememberShelf(group: PaletteGroup, open: boolean): void {
    try {
      localStorage.setItem(`${SHELF_STORAGE_KEY}.${group}`, open ? "1" : "0");
    } catch {
      // Not remembering which shelves were shut is survivable.
    }
  }

  /**
   * Take something off the shelf.
   *
   * It goes onto the cursor, exactly as if it had been picked out of a chest,
   * so putting it away uses the click rules that already exist and there is
   * no second way to move things about. Clicking again tops the stack back up
   * - the shelf never runs out, which is the whole point of it.
   */
  private takeFromStore(id: number, e: MouseEvent): void {
    if (e.button !== 0 && e.button !== 2) return;
    const button = e.button;
    // The right button takes one, the left a full stack - the same halves
    // the rest of the window uses.
    const want = button === 0 ? itemMaxStack(id) : 1;
    if (this.held !== null && this.held.id !== id) {
      // Swapping what is on the cursor for something else would silently
      // destroy it. Put the old one back first.
      return;
    }
    this.held = this.held === null
      ? { id, count: want }
      : { id, count: Math.min(itemMaxStack(id), this.held.count + want) };
    // The cursor only follows the mouse while something is on it, so the
    // first thing lifted off a shelf has to be put under the pointer here or
    // it appears wherever the mouse was last seen holding something.
    this.heldEl.style.left = `${e.clientX}px`;
    this.heldEl.style.top = `${e.clientY}px`;
    this.refresh();
    this.onChange?.();
  }

  get selectedSlot(): number {
    return this.selected;
  }

  select(slot: number): void {
    if (slot < 0 || slot >= HOTBAR_SIZE) return;
    this.selected = slot;
    this.refresh();
    this.sayName();
  }

  /** Move the hotbar selection by `delta`, wrapping around. */
  scrollSelection(delta: number): void {
    this.selected = (this.selected + delta + HOTBAR_SIZE) % HOTBAR_SIZE;
    this.refresh();
    this.sayName();
  }

  /** How long the name of a chosen thing stays over the hotbar. */
  static readonly NAME_MS = 3000;

  /**
   * Say what was just chosen, for three seconds.
   *
   * An empty slot says nothing and takes any name that was showing with it:
   * a name hanging over an empty slot would be the name of the wrong thing.
   * Choosing again restarts the three seconds rather than adding to them.
   */
  private sayName(): void {
    if (this.nameTimer !== null) clearTimeout(this.nameTimer);
    const stack = this.inventory.get(this.selected);
    if (stack === null) {
      this.nameEl.classList.remove("shown");
      this.nameTimer = null;
      return;
    }
    this.nameEl.textContent = itemLabel(stack.id, this.lang);
    this.nameEl.classList.add("shown");
    this.nameTimer = setTimeout(() => {
      this.nameEl.classList.remove("shown");
      this.nameTimer = null;
    }, InventoryUI.NAME_MS);
  }

  toggle(): void {
    this.open ? this.close() : this.show();
  }

  /**
   * Open the bag, optionally over a chest.
   *
   * Passing a chest is how the window becomes a chest window: the same slots,
   * the same held stack, one more row bound to somewhere else.
   */
  show(chest: Inventory | null = null, stove: Furnace | null = null): void {
    this.open = true;
    this.chest = chest;
    this.chestEl.hidden = chest === null;
    this.stove = stove;
    this.stoveEl.hidden = stove === null;
    this.panelEl.hidden = false;
    this.setBookOpen(loadBookPreference());
    this.refresh();
  }

  close(): void {
    // Anything on the cursor or left in the grid goes back to the inventory
    // rather than vanishing.
    if (this.held !== null) {
      const leftover = this.inventory.add(this.held.id, this.held.count);
      this.held = leftover > 0 ? { id: this.held.id, count: leftover } : null;
    }
    clearGridInto(this.grid, this.inventory);

    this.open = false;
    // The chest stays exactly as it was left; only the window forgets it.
    this.chest = null;
    this.chestEl.hidden = true;
    // The fire keeps burning; only the window forgets which stove it was.
    this.stove = null;
    this.stoveEl.hidden = true;
    this.panelEl.hidden = true;
    this.heldEl.hidden = true;
    this.tooltip.hide();
    this.refresh();
    this.onClose?.();
  }
}

export { GRID_SIZE };
